import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import CloseButton from "../../library/Button";
import Button from "../../shared/button/Button";
import {
  Row,
  UsernameInput,
  DomainSelect,
  DomainSpan,
  EmailAddressList,
  EmailAddressListItem,
  EmailAddressValue,
  CenterButtonWrapper,
  EmailError,
  EmailValueButtonWrapper,
  ErrorList,
  ErrorListItem,
  InvalidUsernameError,
} from "../styles/share-modal-styles";

// disabling prop-types because this is a typescript component.
// declaring prop-types + ts interfaces is unneccesary duplication
/* eslint-disable react/prop-types */

export interface SendEmailsViewProps {
  emailDomains: string[];
  emailAddresses: string[];
  setEmailAddresses: (value: string[]) => void;
  handleSend: () => Promise<void>;
  errors: {
    [key: string]: {
      message: string;
    };
  };
  resetErrors: () => void;
}

const SendEmailsView: React.FC<SendEmailsViewProps> = ({
  emailDomains,
  emailAddresses,
  setEmailAddresses,
  handleSend,
  errors,
  resetErrors,
}) => {
  const [username, setUsername] = useState("");
  const [domain, setDomain] = useState(emailDomains[0]);
  const [invalidUsername, setInvalidUsername] = useState(true);

  // this validation is *very* basic, just ensures
  // the client hasn't input an @  sign (as that
  // is already included in the domain) (or a space)
  // and ensures it isn't blank. Difficult to validate
  // much more  than this because most special chars
  // are 'legal' when it comes to emails. We also have
  // a bit of coverage from the server side validation,
  // which uses Devise.email_regexp.
  const validateUsername = (newUsername: string) => {
    const regex = /[ @]/;
    if (newUsername === "" || regex.test(newUsername)) {
      setInvalidUsername(true);
      return;
    }
    setInvalidUsername(false);
  };

  useEffect(() => {
    validateUsername(username);
  }, [username]);

  const showInvalidUsernameError = invalidUsername && username !== "";

  const emailErrorMessage = (emailIndex: number) => {
    const key = `emailAddress${emailIndex}`;
    if (errors && errors[key] && errors[key].message) {
      return errors[key].message;
    }
    return false;
  };

  const errorKeysExclEmail = Object.keys(errors).filter(
    (errorKey) =>
      !emailAddresses
        .map((_, emailIndex) => `emailAddress${emailIndex}`)
        .some((potentialErrorKey) => potentialErrorKey === errorKey)
  );

  const hasMultipleDomains = emailDomains.length > 1;

  return (
    <>
      <Row>
        <UsernameInput
          data-test-id="username-input"
          error={showInvalidUsernameError}
          value={username}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setUsername(event.target.value);
          }}
        />
        {hasMultipleDomains ? (
          <DomainSelect
            id="multiple-domain-select"
            data-test-id="multiple-domain-select"
            value={domain}
            onChange={(event) => {
              setDomain(event.target.value);
            }}
          >
            {emailDomains.map((emailDomain) => (
              <option key={emailDomain} value={emailDomain}>
                {emailDomain}
              </option>
            ))}
          </DomainSelect>
        ) : (
          <DomainSpan data-test-id="single-domain-span">{domain}</DomainSpan>
        )}
        <Button
          secondary
          type="button"
          disabled={invalidUsername}
          buttonName="Add"
          onClickAction={() => {
            const newEmailAddress = `${username}${domain}`;
            if (emailAddresses.indexOf(newEmailAddress) === -1) {
              setEmailAddresses([...emailAddresses, `${username}${domain}`]);
            }
            resetErrors();
            setUsername("");
          }}
        />
      </Row>
      {showInvalidUsernameError && (
        <InvalidUsernameError>not a valid email</InvalidUsernameError>
      )}
      {errorKeysExclEmail.length > 0 && (
        <ErrorList data-test-id="errors-list">
          {errorKeysExclEmail.map((errorKey) => (
            <ErrorListItem key={errorKey}>
              {errors[errorKey].message}
            </ErrorListItem>
          ))}
        </ErrorList>
      )}

      <EmailAddressList>
        {emailAddresses.map((emailAddress, index) => (
          <EmailAddressListItem key={emailAddress}>
            <EmailValueButtonWrapper>
              <EmailAddressValue>{emailAddress}</EmailAddressValue>
              <CloseButton
                icon
                id="remove email button"
                name="remove email"
                type="button"
                aria-label={`Remove ${emailAddress}`}
                onClick={() => {
                  resetErrors();
                  setEmailAddresses(
                    emailAddresses.filter((value) => value !== emailAddress)
                  );
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </CloseButton>
            </EmailValueButtonWrapper>
            {emailErrorMessage(index) && (
              <EmailError data-test-id="email-error">
                {emailErrorMessage(index)}
              </EmailError>
            )}
          </EmailAddressListItem>
        ))}
      </EmailAddressList>

      <CenterButtonWrapper>
        <Button
          primary
          disabled={emailAddresses.length < 1}
          type="button"
          buttonName="Send"
          onClickAction={handleSend}
        />
      </CenterButtonWrapper>
    </>
  );
};

export default SendEmailsView;
